// Catalogue des rôles (port du Rec10Catalogue) — bibliothèque de cartes
// + fiche rôle plein écran (templates / grille & présélection /
// utilisation). v2: éditeur intégré — création de rôles, édition
// structurée des 12 templates (recrutement.catalogue), fourchettes
// éditables (recrutement.salaires).
import { useState } from 'react';
import { createPortal } from 'react-dom';
import type { RecRequisitionDTO, RecRoleDTO } from '../../api/hooks.js';
import { ENTITIES, REC_DIMS, type RecGridCrit } from '@pointage/shared';
import { REC_ST, RecEnt, RecSal, TPL_META, recContrat, recFmtMoney } from './common.js';
import { BandsEditor, RoleModal, TplEditor } from './CatalogueEdit.js';
import { RecModalHost } from './CandidateModals.js';

const DEPT_C: Record<string, string> = { 'Créa': '#c41d8f', 'Tech': '#0bb5ab', 'Commercial': '#d8590f' };
const DEPT_GLYPH: Record<string, string> = { 'Créa': '🎬', 'Tech': '💻', 'Commercial': '🤝' };

type GridCrit = RecGridCrit;
interface PreselCrit { id: string; lbl: string; elim?: boolean }

function firstBand(r: RecRoleDTO): [number, number] | null {
  if (!r.bands) return null;
  const vals = Object.values(r.bands);
  return vals.length ? vals[0]! : null;
}

function Ring({ pct, c }: { pct: number; c: string }) {
  const C = 2 * Math.PI * 17;
  return (
    <span className="cat-ring" style={{ '--rc': c } as React.CSSProperties}>
      <svg width="44" height="44">
        <circle className="bgc" cx="22" cy="22" r="17"></circle>
        <circle className="fgc" cx="22" cy="22" r="17" strokeDasharray={C} strokeDashoffset={C * (1 - pct / 100)}></circle>
      </svg>
      <b>{pct}%</b>
    </span>
  );
}

function CatDetail({ r, reqs, canSal, canCat, onEditTpl }: {
  r: RecRoleDTO; reqs: RecRequisitionDTO[]; canSal: boolean; canCat: boolean;
  onEditTpl: (key: string) => void;
}) {
  const [tab, setTab] = useState<'tpl' | 'grid' | 'use'>('tpl');
  const dc = DEPT_C[r.dept] ?? 'var(--shf)';
  const band = firstBand(r);
  const mine = reqs.filter((q) => q.roleId === r.id);
  const ready = r.tplReady.filter((t) => t.ready).length;
  const grid = (r.templates.grid ?? []) as GridCrit[];
  const presel = (r.templates.presel ?? []) as PreselCrit[];
  const maxCo = Math.max(1, ...grid.map((g) => g.co));

  return (
    <div>
      <div className="cat-hero" style={{ '--dc': dc } as React.CSSProperties}>
        <div className="cat-hstat"><b>{ready}/{r.tplReady.length}</b><span>templates prêts</span><i className="tr"><i style={{ width: `${(ready / r.tplReady.length) * 100}%` }}></i></i></div>
        <div className="cat-hstat"><b>{mine.length}</b><span>recrutement{mine.length > 1 ? 's' : ''} via ce rôle</span></div>
        <div className="cat-hstat"><b>{grid.length}</b><span>critères pondérés</span></div>
        <div className="cat-hstat"><b>{canSal ? (band ? recFmtMoney(band[0]) : '—') : '🔒'}</b><span>bas de fourchette</span></div>
        <div className="cat-hstat"><b>{canSal ? (band ? recFmtMoney(band[1]) : '—') : '🔒'}</b><span>haut de fourchette</span></div>
      </div>
      <div className="rec-seg" style={{ marginBottom: 16 }}>
        {([['tpl', 'Templates hérités'], ['grid', 'Grille & présélection'], ['use', 'Utilisation']] as const).map(([k, l]) => (
          <button key={k} className={tab === k ? 'on' : ''} onClick={() => setTab(k)}>{l}</button>
        ))}
      </div>

      {tab === 'tpl' && (
        <div className="cat-cols">
          <div className="cat-sec">
            <h4>🧬 Les {r.tplReady.length} templates du rôle</h4>
            <p className="cat-note">Chaque recrutement ouvert depuis ce rôle hérite automatiquement de ces templates — modifiables au cas par cas sans toucher au modèle.{canCat ? ' Cliquez un template pour l’éditer.' : ''}</p>
            {r.tplReady.map((t) => {
              const meta = TPL_META[t.key] ?? [t.key, '📄', ''];
              return (
                <div
                  key={t.key} className={`cat-tplrow${t.ready ? '' : ' off'}${canCat ? ' ed' : ''}`}
                  onClick={canCat ? () => onEditTpl(t.key) : undefined}
                  role={canCat ? 'button' : undefined}
                >
                  <span className="ic">{meta[1]}</span>
                  <div><b>{meta[0]}</b><span>{meta[2]}</span></div>
                  {canCat && <span className="cated-editpill">{t.ready ? '✎ Modifier' : '✎ Rédiger'}</span>}
                  <span className={`rec-dst ${t.ready ? 'ok' : 'manq'}`}>{t.ready ? 'Prêt' : 'À rédiger'}</span>
                </div>
              );
            })}
          </div>
          <div className="cat-sec">
            <h4>💰 Fourchette salariale</h4>
            {canSal ? (
              canCat ? <BandsEditor key={r.id} role={r} /> : (
                r.bands && Object.keys(r.bands).length ? (
                  <div>
                    {Object.entries(r.bands).map(([entId, b]) => {
                      const ent = ENTITIES.find((e) => e.id === entId);
                      return (
                        <div key={entId} className="cat-bandrow">
                          {ent && <RecEnt id={ent.id} name={ent.name} color={ent.color} />}
                          <span className="cat-bandbig">{recFmtMoney(b[0])}<span>→</span>{recFmtMoney(b[1])}</span>
                        </div>
                      );
                    })}
                    <p className="cat-note" style={{ marginTop: 10 }}>Toute offre hors fourchette sera signalée à la chaîne de validation — visible RH & direction uniquement.</p>
                  </div>
                ) : <p className="cat-note">Fourchette à définir pour ce rôle.</p>
              )
            ) : <div className="rec-emptybox">🔒 Restreint — visible RH & direction.</div>}
            <h4 style={{ marginTop: 18 }}>🔗 Liaisons du modèle</h4>
            <div className="cat-link"><span className="ic">⭐</span><div><b>Grilles d’évaluation annuelles</b><span>mêmes critères pondérés — continuité recrutement → évaluation</span></div></div>
            <div className="cat-link"><span className="ic">🧳</span><div><b>Plan d’onboarding J1 → J90</b><span>généré à l’acceptation de l’offre</span></div></div>
            <div className="cat-link"><span className="ic">🗓</span><div><b>Période d’essai</b><span>points J+15 → décision re-notés sur cette grille, issue reportée dans Contrats</span></div></div>
          </div>
        </div>
      )}

      {tab === 'grid' && (
        <div className="cat-cols">
          <div className="cat-sec">
            <h4>⭐ Grille d’évaluation — par dimension</h4>
            <p className="cat-note">Chaque critère porte une dimension ; la grille n’est « prête » que si les 4 sont couvertes. Utilisée par les scorecards et les revues d’essai.</p>
            <div className="rv2-cov">
              {REC_DIMS.map((d) => {
                const n = grid.filter((g) => g.dim === d.id).length;
                return <div key={d.id} className={`rv2-covc${n ? '' : ' miss'}`} style={{ '--dc': d.c } as React.CSSProperties}><b><i></i>{d.lbl}</b><div className="n">{n}</div><span>{n ? `critère${n > 1 ? 's' : ''}` : 'à compléter'}</span></div>;
              })}
            </div>
            {grid.map((g) => {
              const d = REC_DIMS.find((x) => x.id === g.dim);
              return (
                <div key={g.id} className="cat-crit">
                  <b>{g.lbl}</b>
                  <span className="tr"><i style={{ width: `${(g.co / maxCo) * 100}%`, background: d?.c ?? dc }}></i></span>
                  <span className="co">×{g.co}</span>
                  {d ? <span className="rv2-dim" style={{ '--dc': d.c } as React.CSSProperties}><i></i>{d.short}</span> : <span className="rec-stpill bad">sans dimension</span>}
                </div>
              );
            })}
            <span className="cat-foot-lbl">Pondération ×2 / ×3 — moyenne pondérée /5 sur chaque scorecard</span>
          </div>
          <div className="cat-sec">
            <h4>🔍 Critères de présélection CV</h4>
            <p className="cat-note">Checklist PASS / FAIL — seul un FAIL sur un critère éliminatoire oriente vers le rejet motivé.</p>
            {presel.map((c) => (
              <div key={c.id} className="cat-presel"><span className="pf" style={c.elim !== false ? { background: 'var(--ink)', color: 'var(--card)' } : undefined}>{c.elim !== false ? 'ÉLIMINATOIRE' : 'INDICATIF'}</span>{c.lbl}</div>
            ))}
          </div>
        </div>
      )}

      {tab === 'use' && (
        <div className="cat-cols">
          <div className="cat-sec">
            <h4>📈 Recrutements ouverts depuis ce rôle</h4>
            {mine.map((q) => {
              const ent = ENTITIES.find((e) => e.id === q.entityId);
              const st = REC_ST[q.status];
              return (
                <div key={q.id} className="cat-use">
                  {ent && <RecEnt id={ent.id} name={ent.name} color={ent.color} />}
                  <div><b>{recContrat(q)} · {q.seniority}</b><span>créée le {q.createdAt.split('-').reverse().join('/')} · {q.motif}</span></div>
                  <span className={`rec-stpill ${st.cls}`}>{st.lbl}</span>
                </div>
              );
            })}
            {!mine.length && <div className="rec-emptybox">Jamais utilisé — ouvrez une demande depuis ce rôle.</div>}
          </div>
          <div className="cat-sec">
            <h4>👥 Vivier associé au rôle</h4>
            <div className="rec-emptybox">Les profils mis au vivier sur ce rôle se retrouvent dans l’onglet Vivier (recherche par rôle ou par tag).</div>
          </div>
        </div>
      )}
    </div>
  );
}

export function RecCatalogue({ roles, reqs, canSal, canCat, dark }: {
  roles: RecRoleDTO[]; reqs: RecRequisitionDTO[]; canSal: boolean; canCat: boolean; dark: boolean;
}) {
  const [dept, setDept] = useState('all');
  const [sel, setSel] = useState<string | null>(null);
  // Édition (v2): template ouvert dans la fiche rôle, modal rôle
  // (création si 'new', sinon id du rôle à modifier).
  const [editTpl, setEditTpl] = useState<string | null>(null);
  const [roleModal, setRoleModal] = useState<'new' | string | null>(null);
  const allDepts = [...new Set(roles.map((r) => r.dept))];
  const depts = ['all', ...allDepts];
  const list = roles.filter((r) => (dept === 'all' || r.dept === dept) && r.active);
  // Les rôles archivés ne sont servis qu'aux gestionnaires du catalogue.
  const archived = roles.filter((r) => !r.active && (dept === 'all' || r.dept === dept));
  const selR = sel ? roles.find((r) => r.id === sel) ?? null : null;
  const modalR = roleModal && roleModal !== 'new' ? roles.find((r) => r.id === roleModal) ?? null : null;
  const closePanel = () => { setSel(null); setEditTpl(null); };

  return (
    <div>
      <div className="rec-ctrl">
        <div className="rec-seg">
          {depts.map((d) => (
            <button key={d} className={dept === d ? 'on' : ''} onClick={() => setDept(d)}>
              {d === 'all' ? 'Tous les départements' : d}
              {d !== 'all' && <span className="cat-dot" style={{ background: DEPT_C[d] }}></span>}
            </button>
          ))}
        </div>
        <span className="rec-hint">Un rôle = 12 templates hérités par chaque recrutement — rien ne se réécrit à la main</span>
        {canCat && (
          <>
            <div className="rec-spacer"></div>
            <button className="rec-go sm" onClick={() => setRoleModal('new')}>＋ Nouveau rôle</button>
          </>
        )}
      </div>
      <div className="cat-grid">
        {list.map((r) => {
          const dc = DEPT_C[r.dept] ?? 'var(--shf)';
          const ready = r.tplReady.filter((t) => t.ready).length;
          const pct = Math.round((ready / 11) * 100);
          const band = firstBand(r);
          const grid = (r.templates.grid ?? []) as GridCrit[];
          return (
            <div key={r.id} className="cat-card" style={{ '--dc': dc } as React.CSSProperties} onClick={() => setSel(r.id)}>
              <div className="cat-top">
                <span className="cat-glyph">{DEPT_GLYPH[r.dept] ?? '📁'}</span>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className="nm">{r.label}</div>
                  <div className="dp"><span className="cat-dept">{r.dept}</span>grille {grid.length} critères pondérés</div>
                </div>
                <Ring pct={pct} c={pct === 100 ? 'var(--green)' : pct >= 70 ? 'var(--warn)' : 'var(--bad)'} />
              </div>
              <div className="cat-nums">
                <div className="n"><b>{r.openCount}</b><span>poste{r.openCount > 1 ? 's' : ''} ouvert{r.openCount > 1 ? 's' : ''}</span></div>
                <div className="n"><b>—</b><span>candidats actifs</span></div>
                <div className="n"><b>—</b><span>au vivier</span></div>
                <div className="n"><b>{ready}<i>/{r.tplReady.length}</i></b><span>templates prêts</span></div>
              </div>
              <div className="cat-band">
                <span className="k">Fourchette salariale</span>
                {canSal
                  ? (band ? <RecSal band={band} /> : <span className="rec-hint">à définir</span>)
                  : <span className="rec-lockpill">🔒 Confidentiel</span>}
              </div>
              <div className="cat-tplbar">{r.tplReady.map((t) => <i key={t.key} className={t.ready ? 'on' : ''} title={TPL_META[t.key]?.[0] ?? t.key}></i>)}</div>
              <div className="cat-foot">
                <button className="rec-go sm">Ouvrir la fiche rôle →</button>
                {ready < 11
                  ? <span className="cat-miss">⚠ {11 - ready} template{11 - ready > 1 ? 's' : ''} à compléter</span>
                  : <span className="cat-ok">✓ prêt à recruter</span>}
              </div>
            </div>
          );
        })}
      </div>

      {canCat && archived.length > 0 && (
        <div className="cated-archsec">
          <div className="rec-lbl">Rôles archivés — hors wizard, restaurables</div>
          <div className="cated-archrow">
            {archived.map((r) => (
              <button key={r.id} className="cated-archcard" onClick={() => setRoleModal(r.id)}>
                <span className="cat-glyph" style={{ '--dc': DEPT_C[r.dept] ?? 'var(--shf)' } as React.CSSProperties}>{DEPT_GLYPH[r.dept] ?? '📁'}</span>
                <span className="nm">{r.label}</span>
                <span className="cated-archpill">Archivé</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {selR && createPortal(
        <div className={`recapp rec-portal${dark ? ' theme-dark' : ''}`}>
          <div className="rec-ov" onClick={closePanel}></div>
          <div className="rec-fullpanel" style={{ '--sc': DEPT_C[selR.dept] ?? 'var(--shf)' } as React.CSSProperties}>
            <div className="rec-fullhead">
              <span className="cat-glyph big">{DEPT_GLYPH[selR.dept] ?? '📁'}</span>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div className="ttl">Fiche rôle — {selR.label}</div>
                <div className="sub">{selR.dept} · {((selR.templates.grid ?? []) as GridCrit[]).length} critères pondérés · {((selR.templates.presel ?? []) as PreselCrit[]).length} critères de présélection</div>
              </div>
              {!selR.active && <span className="cated-archpill">Archivé</span>}
              {canCat && !editTpl && (
                <button className="rec-ghost sm" onClick={() => setRoleModal(selR.id)}>✎ Rôle</button>
              )}
              {selR.tplReady.filter((t) => t.ready).length === 11
                ? <span className="cat-readypill ok">✓ prêt à recruter</span>
                : <span className="cat-readypill">{11 - selR.tplReady.filter((t) => t.ready).length} template(s) manquant(s)</span>}
              <button className="rec-x" onClick={closePanel} aria-label="Fermer">✕</button>
            </div>
            <div className="rec-fullbody">
              {editTpl
                ? <TplEditor role={selR} tplKey={editTpl} onDone={() => setEditTpl(null)} />
                : <CatDetail r={selR} reqs={reqs} canSal={canSal} canCat={canCat} onEditTpl={setEditTpl} />}
            </div>
          </div>
        </div>,
        document.body,
      )}

      {roleModal && (
        <RecModalHost dark={dark}>
          <RoleModal role={modalR} depts={allDepts} onClose={() => setRoleModal(null)} />
        </RecModalHost>
      )}
    </div>
  );
}
